import { CardVariant } from "components/ui/summary-card";
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function titleCase(str: string): string {
  if (!str) return str;
  return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
}

export function upperCase(str: string): string {
  if (!str) return str;
  return str.toUpperCase();
}

export function toIST(date: Date): string {
  const istOffset = 5.5 * 60 * 60 * 1000;
  const ist = new Date(date.getTime() + istOffset);

  return ist.toISOString().slice(0, 19).replace("T", " ");
}

export function formatDateOnly(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { 
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export function formatDateTime(dateStr: string) {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return { date: dateStr, time: '' };
  return {
    date: date.toLocaleDateString('en-US', { 
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }),
    time: date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  };
};

export function formatUTCToIST(dateStr: string): string {
  if (!dateStr) return "";

  const utc = dateStr.endsWith("Z") ? dateStr : `${dateStr}Z`;
  const date = new Date(utc);

  if (Number.isNaN(date.getTime())) return dateStr;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
}

export function formatDate(dateString: string) {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";

  return `${day}${suffix} ${month} ${year}`;
}

export function formatDateWithOrdinal(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;

  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  const year = date.getFullYear();

  const getOrdinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  return `${day}${getOrdinal(day)} ${month} ${year}`;
}



export function formatCount(n: number): string {
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)
}

export function formatINR(v: number): string {
    const absValue = Math.abs(v)
    const isNegative = v < 0
    const prefix = isNegative ? "-" : ""

    if (absValue >= 1e7) {
        return `${prefix}${(absValue / 1e7).toLocaleString("en-IN", {
            maximumFractionDigits: 2,
        })} Cr`
    } else if (absValue >= 1e5) {
        return `${prefix}${(absValue / 1e5).toLocaleString("en-IN", {
            maximumFractionDigits: 2,
        })} L`
    }
    return `${prefix}${absValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
}

export function getVariantTextColor(variant?: CardVariant) {
    switch (variant) {
        case "total":
            return "text-blue-900"
        case "positive":
            return "text-emerald-700"
        case "negative":
            return "text-red-700"
        default:
            return "text-slate-900"
    }
}

export function formatWeight(kg: number): string {
    if (kg >= 1000) {
        return `${(kg / 1000).toFixed(2)} T`
    }
    return `${kg.toFixed(2)} kg`
}